import type { BudgetResource, AppropriatedResource } from "@/services/orcamento";

/** Agregação por insumo (recurso) em uma obra — todas as UCs somadas. */
export interface InsumoOrcamentoObraAgg {
  key: string;
  resourceId: string;
  description: string;
  unit: string;
  qtdOrcada: number;
  orcado: number;
  qtdRealizada: number;
  realizado: number;
}

export interface InsumoOrcamentoComparativoRow {
  key: string;
  label: string;
  unit: string;
  qtdOrcadaA: number;
  qtdOrcadaB: number;
  qtdRealizadaA: number;
  qtdRealizadaB: number;
  /** Preço unitário médio orçado (Σ valor orçado ÷ Σ qtd orçada). */
  precoOrcadoA: number | null;
  precoOrcadoB: number | null;
  /** Preço unitário médio realizado (Σ realizado ÷ Σ qtd realizada). */
  precoRealizadoA: number | null;
  precoRealizadoB: number | null;
  realizadoA: number;
  realizadoB: number;
  /** Realizado ÷ nº de casas da obra (alinhado ao custo unitário gerencial). */
  realizadoPerCasaA: number;
  realizadoPerCasaB: number;
  diffRealizadoPerCasa: number;
  absDiffRealizadoPerCasa: number;
  relSpreadPct: number | null;
}

function resourceKey(resourceId: string, description: string): string {
  const id = (resourceId ?? "").trim();
  if (id) return id;
  return `d:${(description ?? "").trim().toLowerCase().replace(/\s+/g, " ")}`;
}

/**
 * Agrega linhas de orçamento e apropriações por código de recurso (insumo),
 * somando todas as unidades construtivas da obra.
 */
export function aggregateInsumoOrcamentoPorObra(
  budgetResources: BudgetResource[],
  appropriated: AppropriatedResource[],
): Map<string, InsumoOrcamentoObraAgg> {
  const map = new Map<string, InsumoOrcamentoObraAgg>();

  const touch = (key: string, init: Partial<InsumoOrcamentoObraAgg>) => {
    if (!map.has(key)) {
      map.set(key, {
        key,
        resourceId: init.resourceId ?? "",
        description: init.description ?? "",
        unit: init.unit ?? "",
        qtdOrcada: 0,
        orcado: 0,
        qtdRealizada: 0,
        realizado: 0,
      });
    }
    return map.get(key)!;
  };

  for (const r of budgetResources) {
    const key = resourceKey(r.resourceId, r.description);
    if (!key || key === "d:") continue;
    const row = touch(key, {
      resourceId: r.resourceId,
      description: r.description,
      unit: r.unit,
    });
    row.qtdOrcada += r.quantity;
    row.orcado += r.totalOrcado;
    if (!row.description && r.description) row.description = r.description;
    if (!row.resourceId && r.resourceId) row.resourceId = r.resourceId;
    const u = (r.unit ?? "").trim();
    if (!row.unit && u) row.unit = u;
  }

  for (const r of appropriated) {
    const key = resourceKey(r.resourceId, r.description);
    if (!key || key === "d:") continue;
    const row = touch(key, {
      resourceId: r.resourceId,
      description: r.description,
      unit: r.unit,
    });
    row.qtdRealizada += r.quantity;
    row.realizado += r.value;
    if (!row.description && r.description) row.description = r.description;
    if (!row.resourceId && r.resourceId) row.resourceId = r.resourceId;
    const u = (r.unit ?? "").trim();
    if (!row.unit && u) row.unit = u;
  }

  return map;
}

function precoMedio(valor: number, qtd: number): number | null {
  if (qtd > 1e-9 && valor > 0) return valor / qtd;
  return null;
}

export function mergeInsumoOrcamentoComparativo(
  aggA: Map<string, InsumoOrcamentoObraAgg>,
  aggB: Map<string, InsumoOrcamentoObraAgg>,
  unitCountA: number,
  unitCountB: number,
): InsumoOrcamentoComparativoRow[] {
  const keys = new Set<string>([...aggA.keys(), ...aggB.keys()]);
  const ua = unitCountA > 0 ? unitCountA : 0;
  const ub = unitCountB > 0 ? unitCountB : 0;
  const rows: InsumoOrcamentoComparativoRow[] = [];

  for (const key of keys) {
    const a = aggA.get(key);
    const b = aggB.get(key);
    const label =
      (a && b && a.description.length >= b.description.length ? a.description : b?.description) ||
      a?.description ||
      b?.description ||
      key;
    const unit = (a?.unit || b?.unit || "").trim();

    const qtdOrcadaA = a?.qtdOrcada ?? 0;
    const qtdOrcadaB = b?.qtdOrcada ?? 0;
    const qtdRealizadaA = a?.qtdRealizada ?? 0;
    const qtdRealizadaB = b?.qtdRealizada ?? 0;
    const realizadoA = a?.realizado ?? 0;
    const realizadoB = b?.realizado ?? 0;

    const precoOrcadoA = precoMedio(a?.orcado ?? 0, qtdOrcadaA);
    const precoOrcadoB = precoMedio(b?.orcado ?? 0, qtdOrcadaB);
    const precoRealizadoA = precoMedio(realizadoA, qtdRealizadaA);
    const precoRealizadoB = precoMedio(realizadoB, qtdRealizadaB);

    const realizadoPerCasaA = ua > 0 ? realizadoA / ua : 0;
    const realizadoPerCasaB = ub > 0 ? realizadoB / ub : 0;
    const diffRealizadoPerCasa = realizadoPerCasaA - realizadoPerCasaB;
    const absDiffRealizadoPerCasa = Math.abs(diffRealizadoPerCasa);
    const mid = (realizadoPerCasaA + realizadoPerCasaB) / 2;
    const relSpreadPct =
      mid >= 1 ? (absDiffRealizadoPerCasa / mid) * 100 : absDiffRealizadoPerCasa > 0 ? null : 0;

    rows.push({
      key,
      label,
      unit,
      qtdOrcadaA,
      qtdOrcadaB,
      qtdRealizadaA,
      qtdRealizadaB,
      precoOrcadoA,
      precoOrcadoB,
      precoRealizadoA,
      precoRealizadoB,
      realizadoA,
      realizadoB,
      realizadoPerCasaA,
      realizadoPerCasaB,
      diffRealizadoPerCasa,
      absDiffRealizadoPerCasa,
      relSpreadPct,
    });
  }

  return rows
    .filter((r) => r.qtdOrcadaA > 0 || r.qtdOrcadaB > 0 || r.realizadoA > 0 || r.realizadoB > 0)
    .sort((x, y) => y.absDiffRealizadoPerCasa - x.absDiffRealizadoPerCasa);
}
